import { useState } from "preact/hooks";
import type { Character, VariantRules } from "../character";
import type { GuideContext, Settle, VariantOutcome } from "../guide";
import { setVariantRules, VARIANT_RULE_KEYS, VARIANT_RULE_LABELS } from "../guide";

const DESCRIPTIONS: Record<keyof VariantRules, string> = {
  freeArchetype: "An extra class feat at every even Level, for archetype feats only: a dedication, then feats of that archetype; another dedication once two of its feats are held.",
  automaticBonusProgression: "Attack, defense, Perception and save potencies, devastating attacks, skill potencies and an attribute apex arrive by Level; fundamental runes and equipment item bonuses give way to them.",
  gradualAttributeBoosts: "One free attribute boost at every Level from 2 to 5, 7 to 10, 12 to 15 and 17 to 20 instead of four at 5, 10, 15 and 20.",
  ancestryParagon: "An extra ancestry feat at Level 1 and at 3, 7, 11, 15 and 19.",
  dualClass: "A second class at Level 1: both classes' features, the better proficiencies, the higher Hit Points, a key attribute from either, class feats from either class.",
  proficiencyWithoutLevel: "Proficiency bonuses leave the Level out of every statistic; DCs are the GM's to lower.",
};

interface Props {
  ctx: GuideContext;
  busy: boolean;
  /** Why changes are off (a Session is open), or null. */
  disabled: string | null;
  settle: Settle;
  onSave: (character: Character) => Promise<void>;
  onMessage: (message: string | undefined) => void;
}

/**
 * The Character's Variant Rules (issue #12): six toggles, each previewed before it applies. The
 * preview lists the Choices the toggle opens, the filled Choices it removes with their Selections
 * and the Selections it makes illegal and reopens; the user confirms or cancels.
 */
export function VariantRulesPanel({ ctx, busy, disabled, settle, onSave, onMessage }: Props) {
  const [pending, setPending] = useState<{ key: keyof VariantRules; outcome: VariantOutcome } | null>(null);
  const [working, setWorking] = useState(false);
  const rules = ctx.character.variantRules;
  const preview = async (key: keyof VariantRules, on: boolean) => {
    setWorking(true);
    onMessage(undefined);
    try {
      const outcome = await setVariantRules(ctx, { ...rules, [key]: on }, settle);
      setPending({ key, outcome });
    } catch (e) {
      onMessage((e as Error).message);
    } finally {
      setWorking(false);
    }
  };
  const confirm = async () => {
    if (!pending) return;
    const next = pending.outcome.ctx.character;
    setPending(null);
    await onSave(next);
  };
  const off = busy || working || !!disabled;
  return (
    <section class="variant-rules">
      <div class="row">
        <h3>Variant Rules</h3>
        <span class="muted">GM Core, per Character</span>
      </div>
      {disabled && <p class="notice">{disabled}</p>}
      <ul class="plain">
        {VARIANT_RULE_KEYS.map((key) => (
          <li key={key}>
            <label class="toggle">
              <input type="checkbox" checked={pending?.key === key ? pending.outcome.rules[key] : rules[key]} disabled={off} onChange={(e) => void preview(key, (e.target as HTMLInputElement).checked)} />
              <strong>{VARIANT_RULE_LABELS[key]}</strong>
            </label>
            <p class="muted">{DESCRIPTIONS[key]}</p>
          </li>
        ))}
      </ul>
      {pending && <Preview pending={pending} busy={off} onConfirm={() => void confirm()} onCancel={() => setPending(null)} />}
    </section>
  );
}

function Preview({ pending, busy, onConfirm, onCancel }: { pending: { key: keyof VariantRules; outcome: VariantOutcome }; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  const { key, outcome } = pending;
  const on = outcome.rules[key];
  const nothing = outcome.opened.length === 0 && outcome.removed.length === 0 && outcome.moved.length === 0 && outcome.reopened.length === 0;
  return (
    <div class="card pending">
      <p>
        <strong>{on ? "Enabling" : "Disabling"} {VARIANT_RULE_LABELS[key]}</strong>
        {nothing ? " changes no Choice; the sheet's numbers follow the rule." : ":"}
      </p>
      {outcome.opened.length > 0 && (
        <>
          <p>Opens {outcome.opened.length === 1 ? "one Choice" : `${outcome.opened.length} Choices`}:</p>
          <ul class="plain">
            {outcome.opened.map((c) => <li key={c.key}>Level {c.level}: {c.label}</li>)}
          </ul>
        </>
      )}
      {outcome.removed.length > 0 && (
        <>
          <p>Removes {outcome.removed.length === 1 ? "one filled Choice" : `${outcome.removed.length} filled Choices`} with what filled them:</p>
          <ul class="plain">
            {outcome.removed.map((c) => <li key={c.key}>Level {c.level}: {c.label}{c.selection ? <span class="muted"> ({c.selection})</span> : null}</li>)}
          </ul>
        </>
      )}
      {outcome.moved.length > 0 && (
        <>
          <p>Moves {outcome.moved.length === 1 ? "one boost" : `${outcome.moved.length} boosts`} within its set, keeping the numbers:</p>
          <ul class="plain">
            {outcome.moved.map((m) => <li key={m.to}>{m.label}: Level {m.fromLevel} to Level {m.toLevel}</li>)}
          </ul>
        </>
      )}
      {outcome.reopened.length > 0 && (
        <>
          <p>Reopens {outcome.reopened.length === 1 ? "one Selection" : `${outcome.reopened.length} Selections`} that became illegal:</p>
          <ul class="plain">
            {outcome.reopened.map((r) => <li key={`${r.key}#${r.flag ?? ""}`}>Level {r.level}: {r.label} <span class="muted">({r.reason})</span></li>)}
          </ul>
        </>
      )}
      <span class="actions">
        <button type="button" class="primary" disabled={busy} onClick={onConfirm}>{on ? "Enable" : "Disable"}</button>
        <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
      </span>
    </div>
  );
}
